import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  User,
  Link2,
  Copy,
  Check,
  Mail,
  Calendar,
  Edit3,
  Save,
  X,
  Users,
  LogOut
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

export default function ProfileSection({ onBack }) {
  const { user, profile, signOut } = useAuth();
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(profile?.name || "");
  const [saving, setSaving] = useState(false);

  const copyCode = async () => {
    await navigator.clipboard.writeText(profile?.partner_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ name: name.trim() })
        .eq('id', user.id);
      
      if (!error) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Error updating name:', err);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const getRoleLabel = () => {
    if (profile?.role === 'partner') {
      return { label: 'Partner', icon: Users, color: 'indigo' };
    }
    return { label: 'Laiba', icon: Heart, color: 'rose' };
  };

  const roleInfo = getRoleLabel();
  const RoleIcon = roleInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 via-pink-50 to-purple-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 pt-6 pb-20 px-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/80 hover:text-white transition mb-4"
        >
          <X className="w-5 h-5" />
          <span>Close</span>
        </button>
        <div className="text-center">
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            {editing ? (
              <div className="flex items-center justify-center gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-3 py-1 rounded-lg text-slate-800 text-center"
                  autoFocus
                />
                <button
                  onClick={handleSaveName}
                  disabled={saving}
                  className="p-1 bg-white/20 rounded-lg hover:bg-white/30"
                >
                  <Save className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                {profile?.name || "User"}
                <button onClick={() => setEditing(true)} className="p-1 hover:bg-white/20 rounded">
                  <Edit3 className="w-4 h-4 text-white/80" />
                </button>
              </div>
            )}
          </h1>
          <div className={`inline-flex items-center gap-2 mt-2 px-4 py-1.5 rounded-full bg-${roleInfo.color}-400/30 text-white`}>
            <RoleIcon className="w-4 h-4" />
            <span className="font-medium">{roleInfo.label}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 -mt-10 space-y-4 pb-8">
        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-lg p-6"
        >
          <h3 className="font-semibold text-slate-800 mb-4">Account Info</h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-800">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Member since</p>
                <p className="font-medium text-slate-800">
                  {new Date(profile?.created_at || Date.now()).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Partner Code - only for Laiba */}
        {profile?.role !== 'partner' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-lg p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="w-5 h-5 text-indigo-500" />
              <h3 className="font-semibold text-slate-800">Your Partner Code</h3>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Share this code with your partner so they can link to your account and see your progress.
            </p>
            
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-100 rounded-xl px-4 py-3 font-mono text-lg text-center tracking-widest">
                {profile?.partner_code || "Loading..."}
              </div>
              <button
                onClick={copyCode}
                className="p-3 bg-indigo-100 rounded-xl hover:bg-indigo-200 transition"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5 text-indigo-500" />
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Partner Info */}
        {profile?.partner_id && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-lg p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-pink-500" />
              <h3 className="font-semibold text-slate-800">Linked Partner</h3>
            </div>
            <p className="text-sm text-green-600 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Connected with your partner
            </p>
          </motion.div>
        )}

        {/* Sign Out */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={handleSignOut}
          className="w-full bg-white rounded-3xl shadow-lg p-6 flex items-center justify-center gap-3 text-red-500 font-semibold hover:bg-red-50 transition"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </motion.button>
      </div>
    </div>
  );
}